import React from "react";
import locale from "@bonliva-crm/locale";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  makeStyles,
  Typography
} from "@material-ui/core";
import { useSnackbars } from "@hooks";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { ServicePreferenceCard } from "./components/service-preference-card";
import { Option } from "./components/dropdown-menu";
import { useUserProfile } from "@hooks";
import { useServicePreferencesStore } from "./use-service-preferences-store";
import {
  FormValues,
  ServicePreferenceFormModal
} from "./components/service-preference-form-modal";
import {
  ConsultantServicePreference,
  ConsultantServicePreferenceFragment
} from "@bonliva-crm/graphql";
import { ConsultantServicePreferenceStatus } from "@bonliva-crm/shared";
import { useAlertDialog } from "@bonliva-crm/ui";
import { localToImplicit } from "@bonliva-crm/common";

type Props = {
  consultantId: string;
};

export const ServicePreferencesTab: React.FC<Props> = ({ consultantId }) => {
  const classes = useStyles();
  const { showError, showSuccess } = useSnackbars();
  const { inTeam } = useUserProfile();
  const alert = useAlertDialog();
  const {
    formDialog,
    createServicePref,
    updateServicePref,
    deleteServicePref,
    changeServicePrefStatus,
    useServicePrefs
  } = useServicePreferencesStore();

  const servicePrefs = useServicePrefs(consultantId);

  const servicePreferences = servicePrefs.data?.consultantServicePreferences || [];

  const { activeItems, archivedItems } = servicePreferences.reduce(
    (acc, item) => {
      if (item.archived) {
        acc.archivedItems.push(item);
      } else {
        acc.activeItems.push(item);
      }

      return acc;
    },
    {
      activeItems: [] as ConsultantServicePreferenceFragment[],
      archivedItems: [] as ConsultantServicePreferenceFragment[]
    }
  );

  async function onSubmit(data: FormValues) {
    try {
      const input = {
        description: data.description,
        customers:
          data.customers?.map((item) => ({
            Id: item._id,
            Type: item.Type,
            Name: item.Name
          })) || [],
        period: {
          start: localToImplicit(data.period.start),
          end: localToImplicit(data.period.end)
        }
      };

      if (data.id) {
        await updateServicePref({
          variables: {
            id: data.id,
            input
          }
        });
      } else {
        await createServicePref({
          variables: {
            consultantId,
            input
          }
        });
      }

      showSuccess(locale.Saved);

      formDialog.onClose();
    } catch (error) {
      showError(error);
    }
  }

  const getOptions = (item: ConsultantServicePreference): Option[] => {
    const canDelete = inTeam("IT") && !item.deleted;
    const canNullify = item.status !== ConsultantServicePreferenceStatus.Nullified;

    const options = [];

    if (canNullify) {
      options.push({
        label: locale.nullify,
        value: "nullify",
        onClick: () => {
          alert
            .confirm({
              title: locale.AreYouSure,
              description: ""
            })
            .then((result) => {
              if (result) {
                changeServicePrefStatus({
                  variables: {
                    id: item._id,
                    status: ConsultantServicePreferenceStatus.Nullified
                  },
                  onCompleted: () => {
                    showSuccess(locale.Saved);
                  },
                  onError: (error) => {
                    showError(error?.message);
                  }
                });
              }
            });
        },
        disabled: !canNullify
      });
    }

    if (canDelete) {
      options.push({
        label: locale.Delete,
        value: "delete",
        onClick: () => {
          alert
            .confirm({
              title: locale.AreYouSure,
              description: ""
            })
            .then((result) => {
              if (result) {
                deleteServicePref({
                  variables: {
                    id: item._id
                  },
                  onCompleted: () => {
                    showSuccess(locale.Saved);
                  },
                  onError: (error) => {
                    showError(error?.message);
                  }
                });
              }
            });
        },
        disabled: !canDelete
      });
    }

    return options;
  };

  return (
    <div className={classes.wrapper}>
      <ServicePreferenceFormModal
        servicePrefId={formDialog.data?.servicePrefId}
        open={formDialog.open}
        onClose={formDialog.onClose}
        onSubmit={onSubmit}
      />
      <div className={classes.btnWrap}>
        <Button
          variant={"outlined"}
          color={"primary"}
          startIcon={<AddCircleOutlineOutlinedIcon />}
          onClick={() =>
            formDialog.onOpen({
              servicePrefId: undefined
            })
          }
        >
          {locale.servicePreferences.createNew}
        </Button>
      </div>
      <div className={classes.itemsContainer}>
        {activeItems.map((data) => (
          <ServicePreferenceCard
            menuOptions={getOptions(data)}
            data={data}
            onClick={() =>
              formDialog.onOpen({
                servicePrefId: data._id
              })
            }
          />
        ))}
      </div>
      {archivedItems.length > 0 && (
        <Accordion className={classes.arhivedWrapper}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className={""}>
              <Typography variant="h6">{locale.Archived}</Typography>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.itemsContainer}>
              {archivedItems.map((data) => (
                <ServicePreferenceCard
                  menuOptions={getOptions(data)}
                  data={data}
                  onClick={() =>
                    formDialog.onOpen({
                      servicePrefId: data._id
                    })
                  }
                />
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: "4px 7px",
    maxWidth: "850px"
  },
  btnWrap: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    marginBottom: theme.spacing(3)
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%"
  },
  arhivedWrapper: {
    marginTop: 20
  }
}));
